import { GlobalStore, FormStore } from '@roc/feature-app-core';
import { flow, makeObservable, observable, action } from 'mobx';
import { SecureLinkRequest, WireInstructionsService } from '../services/wireInstructionsService';

// Index of the final "Summary" step in the wire instructions stepper:
// 0 Wire Fraud Notice · 1 Bank Information · 2 DocuSign Confirmation · 3 Summary
const SUMMARY_STEP = 3;

// Fields mirror the "Closing Funds Wire Instruction Authorization Form".
const form = {
  fields: {
    // Section 3 — Title Company Wire Instructions
    entityName: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'title company legal name',
    },
    address: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'title company address',
    },
    streetName: {
      value: '',
      error: null,
      rule: '',
      attribute: 'street name',
    },
    city: {
      value: '',
      error: null,
      rule: '',
      attribute: 'city',
    },
    zipCode: {
      value: '',
      error: null,
      rule: '',
      attribute: 'zip code',
    },
    state: {
      value: '',
      error: null,
      rule: '',
      attribute: 'state',
    },
    authorizedContactName: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'authorized contact name',
    },
    authorizedContactTitle: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'authorized contact title',
    },
    directPhone: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'direct phone',
    },
    emailAddress: {
      value: '',
      error: null,
      rule: 'required|email',
      attribute: 'email address',
    },
    titleLicenseNumber: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'title license / escrow number',
    },
    // Section 3 — Beneficiary Bank Information
    bankName: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'receiving / beneficiary bank name',
    },
    routingNumber: {
      value: '',
      error: null,
      rule: 'required|regex:/^\\d{9}$/',
      attribute: 'aba / routing number',
    },
    accountName: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'account name',
    },
    accountNumber: {
      value: '',
      error: null,
      rule: 'required',
      attribute: 'account number',
    },
    referenceId: {
      value: '',
      error: null,
      rule: '',
      attribute: 'reference / memo line',
    },
    // Section 3 — For International Wires Only (if applicable)
    swiftCode: {
      value: '',
      error: null,
      rule: '',
      attribute: 'swift / bic code',
    },
    intermediaryBankName: {
      value: '',
      error: null,
      rule: '',
      attribute: 'intermediary / correspondent bank name',
    },
    intermediaryRoutingNumber: {
      value: '',
      error: null,
      rule: '',
      attribute: 'intermediary aba / routing number',
    },
    escrowAccountCompliance: {
      value: false,
      error: null,
      rule: 'accepted',
      attribute: 'escrow account compliance confirmation',
    },
  },
  meta: {
    isValid: true,
    error: null,
  },
};

export class WireInstructionsStore extends FormStore {
  loanData: any;
  activeStep: number;
  submitted: boolean;
  selectedDocument: File | null;
  private request: SecureLinkRequest;

  private globalStore: GlobalStore;
  private wireInstructionsService: WireInstructionsService;

  constructor(globalStore: GlobalStore) {
    super(form, globalStore);
    this.globalStore = globalStore;
    this.loanData = null;
    this.activeStep = 0;
    this.submitted = false;
    this.selectedDocument = null;
    this.request = null;
    this.wireInstructionsService = new WireInstructionsService();
    makeObservable(this, {
      loanData: observable,
      activeStep: observable,
      submitted: observable,
      selectedDocument: observable,
      fetchData: flow,
      submitWireInstructions: flow,
      setActiveStep: action,
      setSelectedDocument: action,
      setSession: action,
    });
  }

  /** The secure link session's request() from the page (useSecureLinkSession); every call goes through it. */
  setSession(request: SecureLinkRequest) {
    this.request = request;
  }

  *fetchData() {
    try {
      const response = yield this.wireInstructionsService.getWireInstructionsData(this.request);
      this.loanData = response.data;
      // If the form was already completed in a previous visit, skip straight to
      // the summary step instead of letting the user fill/submit it again.
      if (this.isAlreadySubmitted()) {
        this.submitted = true;
        this.activeStep = SUMMARY_STEP;
      }
    } catch (error) {
      // An ended session (401) is shown by the page; anything else gets a toast.
      if (error?.status !== 401) {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'Failed to load wire instructions data. Please try again.',
        });
      }
    }
  }

  private isAlreadySubmitted(): boolean {
    return this.loanData?.submitted;
  }

  validateBankInfoStep(): boolean {
    this.runFormValidation();
    if (!this.form.meta.isValid) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please fix the validation errors.',
      });
    }
    return this.form.meta.isValid;
  }

  *submitWireInstructions(signature: string) {
    try {
      const formData = new FormData();
      const payload = {
        signature,
        userAgent: navigator.userAgent,
        ...this.getFormValues(),
      };
      formData.append('data', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
      if (this.selectedDocument) {
        formData.append('document', this.selectedDocument);
      }
      yield this.wireInstructionsService.submitWireInstructions(this.request, formData);
      this.submitted = true;
      this.activeStep = SUMMARY_STEP;
    } catch (error) {
      if (error?.status === 401) {
        // The session ended (15 minutes, or the link was opened in another window): request() already switched the page
        // to "session ended".
      } else if (error?.status === 409) {
        // Already submitted for this link or loan.
        this.submitted = true;
        this.activeStep = SUMMARY_STEP;
      } else {
        this.globalStore.notificationStore.showErrorNotification({
          message: 'Failed to submit wire instructions. Please try again.',
        });
      }
    }
  }

  setActiveStep(step: number) {
    this.activeStep = step;
  }

  setSelectedDocument(file: File | null) {
    this.selectedDocument = file;
  }
}
